import { darkThemeColors, lightThemeColors, ThemeColors } from './colors'

const baseColors = {
  blueberry_80: '#00001F',
  blueberry_75: '#030330',
  blueberry_70: '#0A1440',
  blueberry_65: '#1E1E61',
  blueberry_60: '#2A2E78',
  blueberry_50: '#364096',
  blueberry_40: '#444FB4',
  blueberry_30: '#6E7FCC',
  blueberry_20: '#DBE7FF',
  blueberry_25: '#92A5FC',
  blueberry_10: '#EDF1FC',
  blueberry_0: '#F8FAFF',

  // Main colors for badge tokens
  green_10: '#EDF5DC',
  green_30: '#8CC94B',
  green_50: '#357806',
  gold_20: '#FEEAC4',
  gold_30: '#F3B962',
  gold_50: '#AB4C11',
  red_20: '#FFDEDE',
  red_30: '#EB989B',
  red_50: '#A63D51',
  teal_10: '#E7F8F8',
  teal_40: '#01798D',
  peri_20: '#DCE0F7',
  peri_50: '#444693',
  oatmeal_10: '#FBFAF9',
  oatmeal_20: '#F5F2EE',
  oatmeal_50: '#4F4D4A',
  white: '#FFFFFF',
  status_periwinkle_60: '#3A3C7A',
  status_periwinkle_80: '#252654',

  // Component
  slider_background_tint_blue: '#01011F',
}

export const lyraSelectLightThemeColors: ThemeColors = {
  ...lightThemeColors,

  // Text
  textActive: baseColors.blueberry_40,

  // Link
  linkDefault: baseColors.blueberry_40,
  linkHover: baseColors.blueberry_50,

  // Background
  backgroundHighlightPrimary: baseColors.blueberry_10,
  backgroundActive: baseColors.blueberry_40,
  backgroundHover: baseColors.blueberry_50,
  backgroundPressed: baseColors.blueberry_60,
  backgroundLoading: baseColors.blueberry_60,
  backgroundTopMatch: baseColors.blueberry_20,

  // Border
  borderHover: baseColors.blueberry_40,
  borderFocus: baseColors.blueberry_40,
  borderInput: baseColors.blueberry_40,
  borderTopMatch: baseColors.blueberry_20,

  // Icon
  iconActive: baseColors.blueberry_40,

  // Primary button
  primaryButtonBackgroundDefault: baseColors.blueberry_40,
  primaryButtonBackgroundHover: baseColors.blueberry_50,
  primaryButtonBackgroundPressed: baseColors.blueberry_60,
  primaryButtonBackgroundLoading: baseColors.blueberry_60,
  primaryButtonBackgroundFocus: baseColors.blueberry_50,

  secondaryButtonBackgroundHover: baseColors.blueberry_10,
  secondaryButtonBackgroundPressed: baseColors.blueberry_20,
  secondaryButtonBackgroundFocus: baseColors.blueberry_10,
  secondaryButtonOutlineDefault: baseColors.blueberry_40, // confirm this change
  secondaryButtonTextActive: baseColors.blueberry_40, // confirm this change

  // Secondary button variants
  secondaryButtonInverseBackgroundHover: baseColors.blueberry_60,
  secondaryButtonInverseBackgroundPressed: baseColors.blueberry_60,
  secondaryButtonInverseBackgroundFocus: baseColors.blueberry_60,
  secondaryButtonInverseOutlineDefault: baseColors.blueberry_20,
  secondaryButtonInverseOutlinePressed: baseColors.blueberry_40,
  secondaryButtonInverseTextActive: baseColors.blueberry_20,

  wordCloudButtonBackgroundSelected: baseColors.blueberry_10,
  wordCloudButtonOutlineHover: baseColors.blueberry_40,
  wordCloudButtonOutlinePressed: baseColors.blueberry_40,
  wordCloudButtonOutlineSelected: baseColors.blueberry_40,
  wordCloudButtonTextPressed: baseColors.blueberry_50,
  wordCloudButtonTextSelected: baseColors.blueberry_50,

  // Input field
  inputOutlineSelected: baseColors.blueberry_20,
  inputOutlineHover: baseColors.blueberry_40,
  inputOutlineFocus: baseColors.blueberry_40,

  // Checkbox
  checkboxBackgroundSelected: baseColors.blueberry_40,
  checkboxBackgroundPressed: baseColors.blueberry_40,
  checkboxOutlineSelected: baseColors.blueberry_40,

  // Radio
  radioBackgroundSelected: baseColors.blueberry_40,
  radioBackgroundSelectedInactive: baseColors.blueberry_20,
  radioTextSelected: baseColors.blueberry_50,
  radioOutlineSelected: baseColors.blueberry_40,
  radioOutlineSelectedInactive: baseColors.blueberry_20,
  radioOutlineFocused: baseColors.blueberry_40,

  // Radio with outline
  radioWithOutlinePrimaryTextHighlight: baseColors.blueberry_60,
  radioWithOutlineBackgroundSelected: baseColors.blueberry_10,
  radioWithOutlineBorderSelected: baseColors.blueberry_40,
  radioWithOutlinePrimaryTextSelected: baseColors.blueberry_50,
  radioWithOutlineSecondaryTextSelected: baseColors.blueberry_40,
  radioWithOutlineBorderHovered: baseColors.blueberry_40,
  radioWithOutlineBorderInactive: baseColors.blueberry_20,

  // Progress indicator
  progressIndicator: baseColors.blueberry_30,
  progressIndicatorBackgroundTop: baseColors.white,
  progressIndicatorBackgroundBottom: baseColors.white,

  // ScrollView Refresher
  scrollViewRefresherLoadingIcon: baseColors.blueberry_30,

  // Gradient Tokens
  gradientBubbleStart: baseColors.blueberry_10,
  gradientBubbleEnd: baseColors.blueberry_0,

  // Page background gradient tokens
  backgroundGradientFirst: baseColors.blueberry_10,

  // Tooltip
  backgroundTooltip: baseColors.blueberry_60,

  // Carousel button
  carouselButtonFilledBackgroundActive: baseColors.blueberry_40,
  carouselButtonFilledBackgroundHover: baseColors.blueberry_50,
  carouselButtonFilledBackgroundPressed: baseColors.blueberry_60,
  carouselButtonFilledBackgroundFocus: baseColors.blueberry_50,

  components: {
    ...lightThemeColors.components,
    activityHeader: {
      headline: {
        border: baseColors.blueberry_30,
      },
    },
    badge: {
      ...lightThemeColors.components.badge,
      // Lyra Select specific badge colors (swapped infoPrimary and infoSecondary)
      infoPrimary: {
        background: lightThemeColors.components.badge.infoSecondary.background,
        text: lightThemeColors.components.badge.infoSecondary.text,
      },
      infoSecondary: {
        background: lightThemeColors.components.badge.infoPrimary.background,
        text: lightThemeColors.components.badge.infoPrimary.text,
      },
      inverse: {
        background: lightThemeColors.components.badge.inverse.background,
        text: lightThemeColors.components.badge.infoSecondary.text,
      },
    },
    alertBanner: {
      ...lightThemeColors.components.alertBanner,
      calm: {
        ...lightThemeColors.components.alertBanner.calm,
        background: baseColors.blueberry_60,
      },
    },
    avatar: {
      ...lightThemeColors.components.avatar,
      tealTheme: { background: baseColors.blueberry_10, text: baseColors.blueberry_50 },
    },
    chatBubble: {
      ...lightThemeColors.components.chatBubble,
      outOfOffice: {
        ...lightThemeColors.components.chatBubble.outOfOffice,
        background: baseColors.blueberry_40,
      },
    },
    coachMark: {
      background: baseColors.blueberry_60,
      arrow: {
        border: baseColors.blueberry_60,
      },
    },
    dateBlock: {
      border: baseColors.blueberry_40,
      text: baseColors.blueberry_40,
      background: baseColors.blueberry_10,
    },
    feedbackStarIcon: {
      ...lightThemeColors.components.feedbackStarIcon,
      default: {
        ...lightThemeColors.components.feedbackStarIcon.default,
        hover: {
          ...lightThemeColors.components.feedbackStarIcon.default.hover,
          border: baseColors.blueberry_40,
        },
        highlighted: {
          fill: baseColors.blueberry_40,
          border: baseColors.blueberry_40,
        },
      },
    },
    focusBoxShadow: {
      ...lightThemeColors.components.focusBoxShadow,
      outerBox: { fill: baseColors.blueberry_40 },
    },
    genericHeader: {
      background: baseColors.blueberry_60,
    },
    lessonPreview: {
      ...lightThemeColors.components.lessonPreview,
      progressBar: {
        fill: baseColors.blueberry_40,
      },
    },
    lessonThumbnail: {
      iconDisplay: {
        background: baseColors.blueberry_20,
      },
      disabledIcon: {
        fill: baseColors.blueberry_30,
      },
      progressBar: {
        fill: baseColors.blueberry_40,
      },
    },
    messageAttachment: {
      innerContainer: {
        background: baseColors.blueberry_50,
      },
    },
    messageItem: {
      ...lightThemeColors.components.messageItem,
      chatBubbleSender: {
        background: baseColors.blueberry_50,
        border: baseColors.blueberry_50,
      },
    },
    aiStatusBadge: {
      ...lightThemeColors.components.aiStatusBadge,
      processing: {
        background: baseColors.blueberry_10,
        text: baseColors.blueberry_50,
      },
    },
    notificationsOverview: {
      notificationIcon: {
        fill: baseColors.blueberry_60,
      },
    },
    platformEngagementQuarterlyDetails: {
      text: baseColors.blueberry_60,
    },
    privacyScreen: {
      textContainer: { background: baseColors.blueberry_60 },
    },
    progressBar: {
      ...lightThemeColors.components.progressBar,
      fill: baseColors.blueberry_20,
    },
    progressBarSecondary: {
      fill: baseColors.blueberry_30,
      background: baseColors.blueberry_20,
    },
    providerCalendar: {
      ...lightThemeColors.components.providerCalendar,
      event: {
        ...lightThemeColors.components.providerCalendar.event,
        bookableHover: {
          background: baseColors.blueberry_10,
          border: baseColors.blueberry_40,
        },
        recurringSession: {
          background: baseColors.blueberry_20,
          border: baseColors.blueberry_20,
          text: baseColors.blueberry_60,
        },
        mirror: {
          background: baseColors.blueberry_10,
          border: baseColors.blueberry_40,
        },
      },
    },
    providerLanguagesSpoken: {
      ...lightThemeColors.components.providerLanguagesSpoken,
      textBadge: {
        text: baseColors.blueberry_50,
      },
    },
    providerBadges: {
      ...lightThemeColors.components.providerBadges,
      preferenceMatchBadge: {
        checkIcon: { fill: baseColors.blueberry_30 },
      },
    },
    s4cLoadingIndicator: {
      text: baseColors.blueberry_60,
    },
    s4cHeader: {
      background: baseColors.blueberry_10,
    },
    s4cTriageBackgroundHeader: {
      background: '#DAECFF',
    },
    s4cTriageMaximumSliderTint: { background: baseColors.white },
    sliderField: {
      text: baseColors.blueberry_50,
      thumb: {
        ...lightThemeColors.components.sliderField.thumb,
        border: baseColors.blueberry_40,
      },
      track: {
        ...lightThemeColors.components.sliderField.track,
        minimumTint: {
          background: baseColors.blueberry_40,
        },
      },
      stepMarker: {
        ...lightThemeColors.components.sliderField.stepMarker,
        filled: {
          background: baseColors.blueberry_10,
        },
      },
      floatingLabel: {
        ...lightThemeColors.components.sliderField.floatingLabel,
        border: baseColors.blueberry_40,
      },
    },
    multicolorSliderField: {
      ...lightThemeColors.components.multicolorSliderField,
      text: baseColors.blueberry_50,
    },
    triageSummary: {
      ...lightThemeColors.components.triageSummary,
      badge: {
        background: baseColors.blueberry_20,
        fill: baseColors.blueberry_50,
      },
    },
    dotsPagination: {
      ...lightThemeColors.components.dotsPagination,
      activeDot: {
        fill: baseColors.blueberry_30,
      },
    },
    toolbar: {
      background: baseColors.blueberry_60,
    },
    treatmentCard: {
      ...lightThemeColors.components.treatmentCard,
      findCounsellorSection: {
        icon: {
          fill: baseColors.blueberry_60,
        },
      },
    },
    unsupportedLanguageDisclaimer: {
      text: baseColors.blueberry_50,
    },
    videoControlButton: {
      ...lightThemeColors.components.videoControlButton,
      disabledColor: {
        fill: baseColors.blueberry_30,
      },
    },
    videoLessonComplete: {
      ...lightThemeColors.components.videoLessonComplete,
      check: { fill: baseColors.blueberry_20 },
      completeText: { text: baseColors.blueberry_20 },
    },
    videoPlayer: {
      ...lightThemeColors.components.videoPlayer,
      minimumTrackTint: { background: baseColors.blueberry_20 },
    },
    welcomeSplashScreen: {
      backButton: { fill: baseColors.blueberry_60 },
    },
    wellnessToolkitEssentials: {
      text: baseColors.blueberry_50,
    },
    zoomParticipantStatus: {
      ...lightThemeColors.components.zoomParticipantStatus,
      ready: {
        ...lightThemeColors.components.zoomParticipantStatus.ready,
        background: baseColors.blueberry_40,
      },
    },
    zoomVideoControls: {
      ...lightThemeColors.components.zoomVideoControls,
      disabled: { background: baseColors.blueberry_40 },
    },
    onboardingNavBarDefaultBackground: {
      background: baseColors.oatmeal_10,
    },
  },
  icons: {
    ...lightThemeColors.icons,
    appIcon: {
      ...lightThemeColors.icons.appIcon,
      background: baseColors.blueberry_60,
    },
    avatarIcon: {
      body: { fill: baseColors.blueberry_10 },
      shadow: { fill: baseColors.blueberry_20 },
      fill: baseColors.blueberry_30,
    },
    checkMarkIcon: {
      fill: baseColors.blueberry_30,
    },
    essentialsIcon: {
      fill: baseColors.blueberry_60,
    },
    fingerprintIcon: {
      fill: baseColors.blueberry_20,
    },
    linkIcon: {
      chain: { fill: baseColors.blueberry_30 },
    },
    liveChatMessagingIcon: {
      fill: baseColors.blueberry_10,
    },
    logoutIcon: {
      rectangle: { fill: baseColors.blueberry_20 },
      arrow: { fill: baseColors.blueberry_40 },
    },
    menuIcon: {
      fill: baseColors.blueberry_60,
    },
    messagingIcon: {
      border: baseColors.blueberry_60,
    },
    notificationIcon: {
      fill: baseColors.blueberry_30,
    },
    progressIcon: {
      fill: baseColors.blueberry_60,
    },
    scoringIcon: {
      ...lightThemeColors.icons.scoringIcon,
      neutral: {
        arrow: { fill: baseColors.blueberry_40 },
        stroke: { fill: baseColors.blueberry_20 },
        fill: baseColors.blueberry_10,
      },
    },
    selfCareIcon: {
      ...lightThemeColors.icons.selfCareIcon,
      fill: baseColors.blueberry_30,
    },
    sessionIcon: {
      fill: baseColors.blueberry_60,
    },
    timeIcon: {
      background: baseColors.blueberry_10,
      clockHandBackground: {
        fill: baseColors.blueberry_20,
      },
      clockHandForeground: {
        fill: baseColors.blueberry_30,
      },
    },
  },
}

// TODO Wait for design for dark theme colors for lyra select
export const lyraSelectDarkThemeColors: ThemeColors = {
  ...darkThemeColors,
  backgroundTooltip: baseColors.blueberry_10,
  backgroundPrimary: baseColors.blueberry_70,
  backgroundHighlightPrimary: '#192257',
  backgroundSecondary: baseColors.blueberry_75,
  backgroundSection: baseColors.blueberry_75,
  backgroundActive: baseColors.blueberry_40,
  backgroundElement: baseColors.blueberry_70,
  backgroundTopMatch: baseColors.blueberry_65,

  backgroundNavHeader: baseColors.blueberry_80,

  textActive: baseColors.blueberry_25,

  iconActive: baseColors.blueberry_25,
  iconDefault: baseColors.blueberry_50,

  inputBackgroundDefault: baseColors.blueberry_75,
  inputOutlineDefault: baseColors.blueberry_50,
  inputOutlineTriageDefault: baseColors.blueberry_50,
  inputOutlineFocus: baseColors.blueberry_30,

  primaryButtonBackgroundDefault: baseColors.blueberry_40,
  primaryButtonBackgroundLoading: baseColors.blueberry_30,
  primaryButtonBackgroundPressed: baseColors.blueberry_30,
  primaryButtonBackgroundFocus: baseColors.blueberry_30,
  primaryButtonBackgroundHover: baseColors.blueberry_50,

  dividerSecondary: baseColors.blueberry_50,

  secondaryButtonBackgroundPressed: baseColors.blueberry_30,
  secondaryButtonTextActive: baseColors.blueberry_25,
  secondaryButtonOutlineDefault: baseColors.blueberry_25,
  carouselButtonFilledBackgroundHover: baseColors.blueberry_50,

  carouselButtonFilledBackgroundActive: baseColors.blueberry_40,

  // Word cloud button tokens
  wordCloudButtonBackgroundDefault: baseColors.blueberry_70,
  wordCloudButtonBackgroundSelected: baseColors.blueberry_60,
  wordCloudButtonOutlineDefault: baseColors.blueberry_50,
  wordCloudButtonOutlinePressed: baseColors.blueberry_30,
  wordCloudButtonOutlineSelected: baseColors.blueberry_30,

  // Cards
  cardBackgroundPrimary: baseColors.blueberry_70,
  cardOutlinePrimary: baseColors.blueberry_50,

  linkDefault: baseColors.blueberry_25,

  borderDefault: baseColors.blueberry_50,
  borderAccent: baseColors.blueberry_40,
  borderFocus: baseColors.blueberry_30,

  dividerPrimary: baseColors.blueberry_50,

  progressIndicator: baseColors.blueberry_30,

  radioWithOutlineBackgroundDefault: baseColors.blueberry_75,
  radioWithOutlineBorderDefault: baseColors.blueberry_50,
  radioWithOutlineBorderSelected: baseColors.blueberry_30,
  radioWithOutlineBackgroundSelected: baseColors.blueberry_60,
  radioWithOutlineBorderHovered: baseColors.blueberry_40,
  radioOutlineDefault: baseColors.blueberry_50,
  radioOutlineSelected: baseColors.blueberry_30,
  radioBackgroundPrimary: baseColors.blueberry_75,
  radioBackgroundSelected: '#DBE4FF',
  radioTextSelected: '#DBE4FF',

  checkboxOutlineDefault: baseColors.blueberry_50,
  checkboxOutlineSelected: baseColors.blueberry_30,
  checkboxBackgroundSelected: baseColors.blueberry_60,
  checkboxCheckSelected: baseColors.blueberry_30,

  // Skeleton tokens
  skeletonBoneColor: baseColors.blueberry_70,
  skeletonHighlightColor: baseColors.blueberry_65,

  components: {
    ...darkThemeColors.components,
    avatar: {
      ...darkThemeColors.components.avatar,
      tealTheme: { background: baseColors.blueberry_10, text: baseColors.blueberry_50 },
    },
    badge: {
      ...darkThemeColors.components.badge,
      // Lyra Select specific badge colors for dark mode (swapped infoPrimary and infoSecondary, plus neutral changes)
      neutral: {
        background: baseColors.status_periwinkle_60,
        text: baseColors.peri_20,
      },
      infoPrimary: {
        background: darkThemeColors.components.badge.infoSecondary.background,
        text: darkThemeColors.components.badge.infoSecondary.text,
      },
      infoSecondary: {
        background: darkThemeColors.components.badge.infoPrimary.background,
        text: darkThemeColors.components.badge.infoPrimary.text,
      },
      inverse: {
        background: baseColors.status_periwinkle_80,
        text: baseColors.peri_20,
      },
      neutralTransparent: {
        background: darkThemeColors.components.badge.neutralTransparent.background,
        text: baseColors.peri_20,
      },
    },
    wellnessToolkitEssentials: {
      text: baseColors.blueberry_25,
    },
    bottomTabBar: {
      background: baseColors.blueberry_80,
    },
    dotsPagination: {
      ...darkThemeColors.components.dotsPagination,
      activeDot: { fill: baseColors.blueberry_40 },
    },
    wellnessGoalsOnboardingNavBar: {
      background: '#030331',
    },
    wellnessCheckInResultsDomainScore: {
      low: {
        outerCircle: {
          fill: baseColors.red_50,
        },
        innerCircle: {
          fill: baseColors.red_30,
        },
        text: baseColors.red_30,
      },
      moderate: {
        outerCircle: {
          fill: baseColors.gold_50,
        },
        innerCircle: {
          fill: baseColors.gold_30,
        },
        text: baseColors.gold_30,
      },
      high: {
        outerCircle: {
          fill: baseColors.green_10,
        },
        innerCircle: {
          fill: baseColors.green_30,
        },
        text: baseColors.green_30,
      },
      default: {
        outerCircle: {
          fill: baseColors.blueberry_60,
        },
        innerCircle: {
          fill: baseColors.blueberry_50,
        },
        text: baseColors.white,
      },
    },
    multicolorSliderField: {
      ...darkThemeColors.components.multicolorSliderField,
      track: {
        ...darkThemeColors.components.multicolorSliderField.track,
        minimumTint: { background: baseColors.blueberry_70 },
        maximumTint: { background: baseColors.blueberry_70 },
      },
    },
    triageSummary: {
      ...darkThemeColors.components.triageSummary,
      badge: {
        background: baseColors.blueberry_60,
        fill: baseColors.blueberry_30,
      },
    },
    sliderField: {
      ...darkThemeColors.components.sliderField,
      thumb: {
        ...darkThemeColors.components.sliderField.thumb,
        border: baseColors.blueberry_30,
      },
      track: {
        ...darkThemeColors.components.sliderField.track,
        minimumTint: {
          background: baseColors.blueberry_30,
        },
        maximumTint: {
          background: baseColors.slider_background_tint_blue,
        },
      },
      floatingLabel: {
        ...darkThemeColors.components.sliderField.floatingLabel,
        border: baseColors.blueberry_50,
      },
    },
    treatmentRecommendation: {
      background: baseColors.blueberry_80,
    },
    treatmentCard: {
      ...darkThemeColors.components.treatmentCard,
      background: baseColors.blueberry_70,
      findCounsellorSection: {
        icon: {
          fill: baseColors.blueberry_60,
        },
      },
    },
    locationSearchNavigation: {
      ...darkThemeColors.components.locationSearchNavigation,
      highlightColor: { background: baseColors.blueberry_40 },
    },
    s4cHeader: {
      ...darkThemeColors.components.s4cHeader,
      background: baseColors.blueberry_75,
    },
    s4cTriageBackgroundHeader: {
      background: baseColors.blueberry_75,
    },
    s4cTriageMaximumSliderTint: {
      background: baseColors.slider_background_tint_blue,
    },
    twoColumnRegistrationLayout: {
      formBackground: {
        background: baseColors.blueberry_80,
      },
    },
    internationalPhoneNumberEntry: {
      countryButtonAndPhoneInputBox: {
        background: baseColors.blueberry_70,
      },
    },
    setupAccountForm: {
      submitButtonContainer: {
        background: baseColors.blueberry_75,
      },
      formBodyPageContainer: {
        background: baseColors.blueberry_75,
      },
    },
    wellnessCheckInResultsEssentialsCard: {
      background: baseColors.blueberry_75,
    },
    onboardingNavBarDefaultBackground: {
      background: baseColors.blueberry_70,
    },
  },
}
